import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import {
  getKidsChurchClassDateRange,
  isKidsChurchClassId,
  type KidsChurchClassId,
} from '../utils/kidsChurchClass';
import { hasKidsChurchStaffRole, hasPermission, Permission } from '../utils/roles';

export interface Kid {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth?: string;
  parent_id?: string;
  parent_name?: string;
  parent_pin?: string | null;
  user_id?: string | null;
  household_id?: string | null;
  household_name?: string | null;
  is_active?: boolean;
  allergies?: string | null;
  medical_conditions?: string | null;
  special_needs?: string | null;
  emergency_contact_name?: string | null;
  emergency_contact_phone?: string | null;
  emergency_contact_relationship?: string | null;
  teacher_notes?: string | null;
}

export type KidsChildrenResult = {
  kids: Kid[];
  totalCount: number;
  canViewParentPins: boolean;
};

export type UseKidsChildrenParams = {
  page: number;
  pageSize: number;
  search?: string;
  isStaffUser?: boolean;
  checkedInOnly?: boolean;
  classFilter?: KidsChurchClassId | '';
};

async function resolveKidsChurchAccess(isStaffOverride: boolean): Promise<{
  currentUserId: string | null;
  isStaff: boolean;
  canViewParentPins: boolean;
}> {
  const { data: authData } = await supabase.auth.getUser();
  const currentUserId = authData?.user?.id ?? null;
  let isStaff = isStaffOverride;
  let viewerRoles: unknown = null;

  if (currentUserId && !isStaff) {
    const { data: staffRecord } = await supabase
      .from('kids_church_staff')
      .select('id')
      .eq('user_id', currentUserId)
      .eq('is_active', true)
      .maybeSingle();
    isStaff = Boolean(staffRecord?.id);
  }

  if (currentUserId && !isStaff) {
    const { data: currentUserProfile } = await supabase
      .from('users')
      .select('roles')
      .eq('id', currentUserId)
      .maybeSingle();
    viewerRoles = currentUserProfile?.roles ?? null;
    isStaff = hasKidsChurchStaffRole(viewerRoles);
  }

  const canViewParentPins =
    isStaff || (viewerRoles != null && hasPermission(viewerRoles, Permission.MANAGE_KIDS_CHURCH));

  return { currentUserId, isStaff, canViewParentPins };
}

export const useKidsChildren = ({
  page,
  pageSize,
  search,
  isStaffUser,
  checkedInOnly = false,
  classFilter = '',
}: UseKidsChildrenParams) => {
  const safePage = Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
  const safePageSize = Number.isFinite(pageSize) && pageSize > 0 ? Math.floor(pageSize) : 100;
  const from = (safePage - 1) * safePageSize;
  const to = from + safePageSize - 1;
  const normalizedSearch = search?.trim();
  const safeSearch = normalizedSearch ? normalizedSearch.replace(/,/g, '') : '';
  const isStaffOverride = Boolean(isStaffUser);
  const checkedInOnlyFilter = Boolean(checkedInOnly);
  const selectedClass = classFilter && isKidsChurchClassId(classFilter) ? classFilter : '';
  const classDateRange = selectedClass ? getKidsChurchClassDateRange(selectedClass) : null;

  return useQuery<KidsChildrenResult>({
    queryKey: [
      'kids-children',
      safePage,
      safePageSize,
      safeSearch,
      isStaffOverride,
      checkedInOnlyFilter,
      selectedClass,
      classDateRange?.maxDob ?? '',
      classDateRange?.minDobExclusive ?? '',
    ],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { currentUserId, isStaff, canViewParentPins } = await resolveKidsChurchAccess(isStaffOverride);

      let checkedInChildIds: string[] | null = null;
      if (checkedInOnlyFilter) {
        const { data: openCheckinRows, error: openCheckinError } = await supabase
          .from('kids_church_checkins')
          .select('child_id')
          .is('check_out_time', null);
        if (openCheckinError) throw openCheckinError;

        checkedInChildIds = Array.from(
          new Set(
            (openCheckinRows ?? [])
              .map((row: { child_id?: string | null }) => row.child_id)
              .filter((id): id is string => Boolean(id))
          )
        );

        if (checkedInChildIds.length === 0) {
          return { kids: [], totalCount: 0, canViewParentPins };
        }
      }

      let searchParentIds: string[] = [];
      if (safeSearch) {
        const { data: parentRows } = await supabase
          .from('users')
          .select('id')
          .ilike('full_name', `%${safeSearch}%`);
        searchParentIds = (parentRows ?? [])
          .map((row: { id?: string | null }) => row.id)
          .filter((id): id is string => Boolean(id));
      }

      const buildChildrenQuery = (includeCount: boolean) => {
        let query = includeCount
          ? supabase
              .from('kids_church_children')
              .select('*', { count: 'exact' })
              .order('first_name', { ascending: true })
              .range(from, to)
          : supabase
              .from('kids_church_children')
              .select('*')
              .order('first_name', { ascending: true })
              .range(from, to);

        if (checkedInChildIds) {
          query = query.in('id', checkedInChildIds);
        }

        if (classDateRange) {
          query = query.lte('date_of_birth', classDateRange.maxDob);
          if (classDateRange.minDobExclusive) {
            query = query.gt('date_of_birth', classDateRange.minDobExclusive);
          }
        }

        if (safeSearch) {
          const parentClause = searchParentIds.length
            ? `,parent_id.in.(${searchParentIds.join(',')})`
            : '';
          query = query.or(
            `first_name.ilike.%${safeSearch}%,last_name.ilike.%${safeSearch}%${parentClause}`
          );
        }

        return query;
      };

      const { data: children, error, count } = await buildChildrenQuery(true);
      if (error) throw error;

      const householdChildMembers: Array<{
        user_id: string;
        household_id: string;
      }> = [];

      if (currentUserId) {
        if (isStaff) {
          const { data: householdChildren } = await supabase
            .from('household_members')
            .select('user_id, household_id')
            .in('relationship', ['Child', 'child']);
          (householdChildren ?? []).forEach((row) => {
            if (row.user_id && row.household_id) householdChildMembers.push(row);
          });
        } else {
          const { data: memberRow } = await supabase
            .from('household_members')
            .select('household_id')
            .eq('user_id', currentUserId)
            .order('is_primary', { ascending: false })
            .order('joined_at', { ascending: true })
            .limit(1)
            .maybeSingle();
          if (memberRow?.household_id) {
            const { data: householdChildren } = await supabase
              .from('household_members')
              .select('user_id, household_id')
              .in('relationship', ['Child', 'child'])
              .eq('household_id', memberRow.household_id);
            (householdChildren ?? []).forEach((row) => {
              if (row.user_id && row.household_id) householdChildMembers.push(row);
            });
          }
        }
      }

      let existingUserIds = new Set<string>();
      if (householdChildMembers.length) {
        const { data: existingRows } = await supabase
          .from('kids_church_children')
          .select('user_id');
        existingUserIds = new Set(
          (existingRows ?? [])
            .map((child: { user_id?: string | null }) => child.user_id)
            .filter((id): id is string => Boolean(id))
        );
      } else {
        existingUserIds = new Set(
          (children ?? [])
            .map((child: { user_id?: string | null }) => child.user_id)
            .filter((id): id is string => Boolean(id))
        );
      }
      const missingMembers = householdChildMembers.filter((member) => !existingUserIds.has(member.user_id));

      if (missingMembers.length) {
        await Promise.all(
          missingMembers.map(async (member) => {
            const { error: syncError } = await supabase.functions.invoke('create-child-account', {
              body: {
                existingUserId: member.user_id,
                householdId: member.household_id,
              },
            });
            if (syncError) {
              console.warn('[KidsChurch] Failed to sync household child', syncError);
            }
          })
        );
      }

      let kidsList = children ?? [];
      if (missingMembers.length) {
        const { data: refreshedKids } = await buildChildrenQuery(false);
        if (refreshedKids) kidsList = refreshedKids as Kid[];
      }

      const parentIds = Array.from(
        new Set((kidsList ?? []).map((child: Kid) => child.parent_id).filter(Boolean) as string[])
      );
      const parentsMap: Record<string, { name: string; pin: string | null }> = {};
      if (parentIds.length) {
        if (canViewParentPins) {
          const { data: parents } = await supabase
            .from('users')
            .select('id, full_name, kids_church_pin')
            .in('id', parentIds);
          parents?.forEach((parent) => {
            parentsMap[parent.id] = {
              name: parent.full_name,
              pin: parent.kids_church_pin ?? null,
            };
          });
        } else {
          const { data: parents } = await supabase
            .from('users')
            .select('id, full_name')
            .in('id', parentIds);
          parents?.forEach((parent) => {
            parentsMap[parent.id] = {
              name: parent.full_name,
              pin: null,
            };
          });
        }
      }

      const kidsUserIds = kidsList
        .map((child: Kid) => child.user_id)
        .filter((id): id is string => Boolean(id));

      const householdLookup = new Map<
        string,
        {
          household_id: string;
          household_name?: string | null;
        }
      >();
      if (kidsUserIds.length) {
        const { data: memberships } = await supabase
          .from('household_members')
          .select('user_id, household_id, relationship, household:households(name)')
          .in('user_id', kidsUserIds)
          .in('relationship', ['Child', 'child']);
        (memberships ?? []).forEach((row: any) => {
          if (!row.user_id || !row.household_id) return;
          const householdName = Array.isArray(row.household)
            ? row.household[0]?.name
            : row.household?.name;
          householdLookup.set(row.user_id, {
            household_id: row.household_id,
            household_name: householdName ?? null,
          });
        });
      }

      return {
        kids: (kidsList ?? []).map((c: Kid) => {
          const householdInfo = c.user_id ? householdLookup.get(c.user_id) : undefined;
          const parent = c.parent_id ? parentsMap[c.parent_id] : undefined;
          return {
            ...c,
            parent_name: parent?.name ?? (c.parent_id || undefined),
            ...(canViewParentPins ? { parent_pin: parent?.pin ?? null } : {}),
            household_id: householdInfo?.household_id ?? null,
            household_name: householdInfo?.household_name ?? null,
          } as Kid;
        }),
        totalCount: count ?? 0,
        canViewParentPins,
      };
    },
  });
};
